import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getApiBase } from "../../helpers/apiBase";
import { LOCATION_CATEGORIES } from "../../helpers/locationCategories";

/**
 * «Миры» — список игровых локаций платформы.
 *
 * Переделано по просьбе Максима 21.08.2026. Раньше раздел назывался «Список
 * локаций» и стоял на шаблоне карточек товара из купленной темы: описание в
 * несколько строк, цена, кнопки — от интернет-магазина.
 *
 * Теперь на карточке ровно то, по чему выбирают локацию: картинка, название
 * и две цифры — сколько в ней квестов и сколько участников. Описание убрано
 * намеренно: в сетке из четырёх колонок его не читают, оно только шумит.
 *
 * Сверху — фильтр по тематикам. Показываются только те, которые реально
 * встречаются в списке: строка с полусотней пустых кнопок мешает больше,
 * чем помогает.
 */

const PUBLIC = process.env.PUBLIC_URL || "";

const LocationList = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [category, setCategory] = useState(null);

  useEffect(() => {
    const load = async () => {
      const base = getApiBase();
      if (!base) {
        setError("Не задан REACT_APP_FW_API для запросов к API.");
        return;
      }
      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("token");
        const headers = { "Content-Type": "application/json" };
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
        const res = await fetch(`${base}/v1/cpai/location-list`, {
          method: "POST",
          headers,
          body: JSON.stringify({}),
        });
        const data = await res.json().catch(() => ({}));
        if (res.status === 401) {
          setError("Вы не авторизованы или сессия истекла. Перезайдите в кабинет.");
          return;
        }
        if (!res.ok) {
          setError(
            data && data.message
              ? data.message
              : `Не удалось загрузить локации (код ${res.status}).`
          );
          return;
        }
        setLocations(Array.isArray(data) ? data : []);
      } catch (e) {
        setError("Ошибка сети при загрузке локаций.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  // Показываем только те тематики, что реально есть в списке.
  const tabs = useMemo(() => {
    const present = new Set(locations.map((l) => Number(l.category)).filter(Boolean));
    return LOCATION_CATEGORIES.filter((c) => present.has(c.id));
  }, [locations]);

  const shown = useMemo(
    () => (category ? locations.filter((l) => Number(l.category) === category) : locations),
    [locations, category]
  );

  return (
    <div className="fw-page">
      <div className="fw-worlds-head">
        <div>
          <h1 className="fw-title">Миры</h1>
          <p className="fw-sub mb-0">
            Игровые локации платформы. Откройте любую, чтобы увидеть квесты и участников.
          </p>
        </div>
        <Link className="fw-btn" to={`${PUBLIC}/location-list/add`}>
          Создать мир
        </Link>
      </div>

      {tabs.length > 1 && (
        <div className="fw-worlds-filter" role="tablist">
          <button
            type="button"
            role="tab"
            className="fw-ptab"
            aria-selected={category === null}
            onClick={() => setCategory(null)}
          >
            Все
          </button>
          {tabs.map((c) => (
            <button
              key={c.id}
              type="button"
              role="tab"
              className="fw-ptab"
              aria-selected={category === c.id}
              onClick={() => setCategory(c.id)}
            >
              {c.title}
            </button>
          ))}
        </div>
      )}

      {loading && <p className="fw-sec-sub">Загрузка…</p>}

      {error && !loading && (
        <section className="fw-card fw-empty">
          <b>Не удалось загрузить миры</b>
          <p>{error}</p>
        </section>
      )}

      {!loading && !error && shown.length === 0 && (
        <section className="fw-card fw-empty">
          <b>{category ? "В этой тематике пока пусто" : "Миров пока нет"}</b>
          <p>
            {category
              ? "Выберите другую тематику или посмотрите все."
              : "Создайте первый мир — он появится здесь вместе с квестами и участниками."}
          </p>
        </section>
      )}

      {!loading && !error && shown.length > 0 && (
        <div className="fw-worlds">
          {shown.map((item) => (
            <Link
              key={item.id}
              className="fw-world"
              to={`${PUBLIC}/location/dashboard?location_id=${item.id}`}
            >
              <div className="fw-world-pic">
                {item.image32 || item.image ? (
                  <img src={item.image32 || item.image} alt={item.name || ""} />
                ) : (
                  <span className="fw-world-blank">{(item.name || "?").slice(0, 1)}</span>
                )}
              </div>

              <div className="fw-world-body">
                <b>{item.name}</b>
                <div className="fw-world-stats">
                  {typeof item.quest_count === "number" && (
                    <span>
                      {item.quest_count} {plural(item.quest_count, "квест", "квеста", "квестов")}
                    </span>
                  )}
                  {typeof item.users_count === "number" && (
                    <span>
                      {item.users_count} {plural(item.users_count, "участник", "участника", "участников")}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

/** «1 квест», «2 квеста», «5 квестов» — иначе подпись читается как машинная. */
function plural(n, one, few, many) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

export default LocationList;
